import { zodResolver } from '@hookform/resolvers/zod';
import {
  createFileRoute,
  useBlocker,
  useNavigate,
} from '@tanstack/react-router';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { KeyRound, Loader2, Lock } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AccountTwoFactorSummary } from './-components/account-two-factor-summary';
import { SecurityRowHeader, SecurityRowTile } from './-components/security-row';
import { SettingsSection } from './-components/settings-section';
import type { AccountName, ChangePassword } from '@minty/shared/user';
import { useConfirm } from '~/components/confirm-dialog';
import {
  createAccountNameSchema,
  createChangePasswordSchema,
} from '@minty/shared/user';
import { Button } from '~/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '~/components/ui/collapsible';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/components/ui/form';
import { Input } from '~/components/ui/input';
import { Skeleton } from '~/components/ui/skeleton';
import { authClient } from '~/lib/auth-client';
import { notify } from '~/lib/toast';
import { useTRPC } from '~/lib/trpc';

export const Route = createFileRoute('/_dashboard/settings/account')({
  pendingComponent: AccountSettingsPending,
  component: AccountSettingsPage,
});

function AccountSettingsPage() {
  const { t } = useTranslation('settings');
  const trpc = useTRPC();
  const { data: currentUser } = useSuspenseQuery(
    trpc.auth.session.getCurrentUser.queryOptions(),
  );

  const { data: flags } = useSuspenseQuery(
    trpc.config.queries.getFeatureFlags.queryOptions(),
  );

  return (
    <>
      <AccountNameForm name={currentUser.name} email={currentUser.email} />
      <SettingsSection.Wrapper>
        <SettingsSection.Header>
          <SettingsSection.Title>
            {t('Sign-in and security')}
          </SettingsSection.Title>
        </SettingsSection.Header>
        <div className="divide-y divide-border">
          <AccountPasswordForm />
          {flags.twoFactor && (
            <AccountTwoFactorSummary enabled={currentUser.twoFactorEnabled} />
          )}
        </div>
      </SettingsSection.Wrapper>
    </>
  );
}

function AccountNameForm({
  email,
  name,
}: {
  email: string | null;
  name: string;
}) {
  const { t } = useTranslation('settings');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const form = useForm<AccountName>({
    resolver: zodResolver(createAccountNameSchema(t)),
    defaultValues: { name },
    values: { name },
    resetOptions: { keepDirtyValues: true },
  });

  const shouldWarnAboutUnsavedChanges =
    form.formState.isDirty && !form.formState.isSubmitting;

  useBlocker({
    shouldBlockFn: async () => {
      if (!shouldWarnAboutUnsavedChanges) {
        return false;
      }

      return !(await confirm({
        title: t('You have unsaved changes. Leave without saving?'),
        confirmLabel: t('Leave without saving', { ns: 'translation' }),
        isDestructive: true,
      }));
    },
    enableBeforeUnload: shouldWarnAboutUnsavedChanges,
  });

  async function handleSubmit(values: AccountName) {
    form.clearErrors('root');

    const { error } = await authClient.updateUser({ name: values.name });

    if (error) {
      form.setError('root', { message: error.message });
      return;
    }

    form.reset(values);
    // The name is read from the session, which no table change announces.
    await queryClient.invalidateQueries({
      queryKey: trpc.auth.session.getCurrentUser.queryKey(),
    });
    notify.success(t('Your name has been saved.'));
  }

  const rootErrorMessage = form.formState.errors.root?.message;
  const isSubmitting = form.formState.isSubmitting;
  const isDirty = form.formState.isDirty;

  return (
    <SettingsSection.Wrapper>
      <SettingsSection.Header>
        <SettingsSection.Title>{t('Your details')}</SettingsSection.Title>
      </SettingsSection.Header>

      <Form.Root form={form} onSubmit={handleSubmit}>
        <SettingsSection.ContentWrapper className="space-y-4">
          <SettingsSection.Input.Row>
            <SettingsSection.Input.Wrapper>
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="gap-2">
                    <FormLabel>{t('First and last name')}</FormLabel>
                    <FormControl>
                      <Input
                        autoComplete="name"
                        className="text-sm font-normal"
                        {...field}
                      />
                    </FormControl>
                    {/* text-xs to match the email helper beside it. */}
                    <FormDescription className="text-xs">
                      {t('The name your colleagues see next to your work')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SettingsSection.Input.Wrapper>

            <SettingsSection.Input.Wrapper>
              <SettingsSection.Input.Label htmlFor="account-email">
                {t('Email')}
              </SettingsSection.Input.Label>
              <div className="relative">
                <Input
                  id="account-email"
                  value={email ?? ''}
                  readOnly
                  aria-describedby="account-email-description"
                  className="bg-muted pr-9 text-sm font-normal text-muted-foreground"
                />
                <Lock
                  aria-hidden
                  className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground"
                />
              </div>
              <SettingsSection.Input.Description
                id="account-email-description"
                className="mt-2 mb-0"
              >
                {t('Your clinic administrator changes this address.')}
              </SettingsSection.Input.Description>
            </SettingsSection.Input.Wrapper>
          </SettingsSection.Input.Row>

          {rootErrorMessage && (
            <p className="text-sm font-medium text-destructive">
              {rootErrorMessage}
            </p>
          )}
        </SettingsSection.ContentWrapper>

        <SettingsSection.Footer className="justify-end gap-4">
          <SettingsSection.FooterButton
            type="submit"
            disabled={!isDirty || isSubmitting}
          >
            {isSubmitting && (
              <Loader2 className="animate-spin" strokeWidth={2.5} />
            )}
            {t('Save changes')}
          </SettingsSection.FooterButton>
        </SettingsSection.Footer>
      </Form.Root>
    </SettingsSection.Wrapper>
  );
}

function AccountPasswordForm() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const form = useForm<ChangePassword>({
    resolver: zodResolver(createChangePasswordSchema(t)),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  });

  function collapse() {
    form.reset();
    setOpen(false);
  }

  // No confirmation dialog: the submit button's own label says it signs out.
  async function handleSubmit(values: ChangePassword) {
    form.clearErrors('root');

    const { error } = await authClient.changePassword({
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
      // Other sessions are revoked separately below: revoking here would end
      // this session too, before the sign-out we navigate to.
    });

    if (error) {
      if (error.code === 'INVALID_PASSWORD') {
        form.setError('currentPassword', {
          message: t('That is not your current password.'),
        });
        return;
      }

      form.setError('root', { message: error.message });
      return;
    }

    const revoked = await authClient.revokeOtherSessions();

    if (revoked.error) {
      collapse();
      notify.error(
        t(
          'Your password has been changed, but your other devices are still signed in.',
        ),
      );
      return;
    }

    await navigate({ to: '/sign-out' });
  }

  const rootErrorMessage = form.formState.errors.root?.message;
  const isSubmitting = form.formState.isSubmitting;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <SecurityRowHeader
        tile={<SecurityRowTile icon={<KeyRound />} />}
        title={t('Password')}
        description={t(
          'Changing your password signs you out of every device, including this one.',
        )}
        action={
          !open && (
            <CollapsibleTrigger asChild>
              <Button type="button" variant="secondary" size="sm">
                {t('Change password')}
              </Button>
            </CollapsibleTrigger>
          )
        }
      />

      <CollapsibleContent>
        <Form.Root form={form} onSubmit={handleSubmit}>
          <div className="space-y-4 px-5 sm:pl-18">
            <SettingsSection.Input.Wrapper>
              <FormField
                control={form.control}
                name="currentPassword"
                render={({ field }) => (
                  <FormItem className="gap-2">
                    <FormLabel>{t('Current password')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="current-password"
                        className="text-sm font-normal"
                        autoFocus
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SettingsSection.Input.Wrapper>

            <SettingsSection.Input.Wrapper>
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem className="gap-2">
                    <FormLabel>{t('New password')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        className="text-sm font-normal"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      {t('At least 8 characters.')}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SettingsSection.Input.Wrapper>

            <SettingsSection.Input.Wrapper>
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem className="gap-2">
                    <FormLabel>{t('Repeat new password')}</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        className="text-sm font-normal"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </SettingsSection.Input.Wrapper>

            {rootErrorMessage && (
              <p className="text-sm font-medium text-destructive">
                {rootErrorMessage}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-3 px-5 py-4">
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={collapse}
            >
              {t('Cancel')}
            </Button>
            <SettingsSection.FooterButton type="submit" disabled={isSubmitting}>
              {isSubmitting && (
                <Loader2 className="animate-spin" strokeWidth={2.5} />
              )}
              {t('Change password and sign out')}
            </SettingsSection.FooterButton>
          </div>
        </Form.Root>
      </CollapsibleContent>
    </Collapsible>
  );
}

function AccountSettingsPending() {
  const { t } = useTranslation('settings');

  return (
    <SettingsSection.Wrapper aria-busy="true">
      <SettingsSection.Header>
        <SettingsSection.Title>{t('Your details')}</SettingsSection.Title>
      </SettingsSection.Header>
      <SettingsSection.ContentWrapper>
        <Skeleton className="h-9 w-full max-w-[318px] rounded-md" />
      </SettingsSection.ContentWrapper>
      <SettingsSection.Footer className="justify-end gap-4">
        <SettingsSection.FooterButton type="button" disabled>
          {t('Save changes')}
        </SettingsSection.FooterButton>
      </SettingsSection.Footer>
    </SettingsSection.Wrapper>
  );
}
